// soft-pastel — bs01 "Beshik" newborn / beshik to'yi card.
//
// Tender nursery palette: warm parchment with sage olive accent. A swaddled
// crescent moon-cradle sits in the centre; the baby's name renders in Lora,
// with a "BESHIK TO'YI" eyebrow above and a city line below.
import { Photo, type LayoutCtx } from "./_shared";

export function SoftPastelLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(circle at 50% 18%, ${accent}1F 0%, ${bg} 55%)`,
        padding: "5%",
        display: "flex",
      }}
    >
      {/* Deckle-edged paper frame */}
      <div
        style={{
          flex: 1,
          position: "relative",
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 6px ${bg}, inset 0 0 0 7px ${accent}22`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          BESHIK TO'YI
        </div>

        {/* Crescent cradle */}
        <div style={{ width: "62%", aspectRatio: "1 / 1", marginBottom: "5%", position: "relative" }}>
          <Photo
            src={photo}
            style={{ position: "absolute", inset: "14%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", display: "block" }}>
                <defs>
                  <radialGradient id="cradleMist" cx="50%" cy="55%">
                    <stop offset="0%" stopColor={bg} />
                    <stop offset="100%" stopColor={`${accent}22`} />
                  </radialGradient>
                </defs>
                {/* Star sprinkle */}
                {[
                  { x: 18, y: 22 }, { x: 82, y: 26 }, { x: 28, y: 14 },
                  { x: 72, y: 12 }, { x: 50, y: 8 },
                ].map((s, i) => (
                  <g key={i} transform={`translate(${s.x} ${s.y})`} fill={accent} opacity="0.6">
                    <path d="M0 -2 L 0.6 -0.6 L 2 0 L 0.6 0.6 L 0 2 L -0.6 0.6 L -2 0 L -0.6 -0.6 Z" />
                  </g>
                ))}
                <circle cx="50" cy="55" r="36" fill="url(#cradleMist)" stroke={accent} strokeWidth="0.5" opacity="0.5" />
                {/* Crescent moon cradle */}
                <path
                  d="M22 58 Q 24 84 50 86 Q 76 84 78 58 Q 64 70 50 70 Q 36 70 22 58 Z"
                  fill={accent} opacity="0.7"
                />
                {/* Bundled baby — small swaddle */}
                <g transform="translate(50 64)">
                  <ellipse cx="0" cy="0" rx="14" ry="8" fill={bg} stroke={accent} strokeWidth="0.5" />
                  <circle cx="0" cy="-4" r="4" fill={bg} stroke={accent} strokeWidth="0.5" />
                </g>
                {/* Rocker base */}
                <path d="M16 84 Q 50 92 84 84" fill="none" stroke={accent} strokeWidth="0.8" opacity="0.8" />
              </svg>
            }
          />
        </div>

        <div style={{ fontFamily: font, fontSize: 32, lineHeight: 1.05, color: ink, fontWeight: 500 }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          BESHIK TO'YI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
